import '../Login-Screen/Login.css';
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import useScrollOrNavigate from "../../Hooks/useScrollOrNavigate.js";

import Footer from "../../Utils/Footer/footer-component";
import LazyOnView from "../../Utils/LazyLoading/LazyOnView.js";
import Layout from "../../Utils/Navbar/navbar-layout-component.js";

import Contact from "../Contact-screen/Contact.js";
import ReviewList from "../../Components/review-list-component.js";
import { getReviewsData } from "../../Public/reviews-data.js";
import MapSection from "../../Utils/Sections/section-map-component.js";
import FaqSection from "../../Utils/Sections/section-faq-component.js";
import TrustSection from "../../Utils/Sections/section-trust-component.js";
import ContentSection from "../../Utils/Content/content-section-component.js";
import MetricsSection from "../../Utils/Sections/section-metrics-component.js";
import QuickCallSection from "../../Utils/Sections/section-quick-call-component.js";
import ProductListSection from "../../Utils/Sections/section-product-list-component.js";
import CategoryListSection from "../../Utils/Sections/section-category-list-component.js";
import SpecialsBannerSection from "../../Utils/Sections/section-specials-banner-component.js";
import CategoriesBentoSection from "../../Utils/Sections/section-categories-bento-component.js";
import CategoryBanners from "../../Utils/Sections/section-category-banners-component.js";
import InfoBarSection from "../../Utils/Sections/section-info-bar-component.js";

import { getLandingData } from "../../Public/landing-data.js";
import ImageGrid from '../../Utils/Grids/ImageGrid/image-grid-component.js';
import { LoadingAnimation } from "../../Utils/Progress/progress-component.js";
import OurWorkSection from "../../Utils/Sections/section-our-work-component.js";
import ProductFinderSection from "../../Utils/Sections/section-product-finder-component.js";
import ShopHeroSection from "../../Utils/Sections/section-shop-hero-component.js";
import LandingHeroSection from './landing-hero-section.js';
import useRenderLog from '../../Hooks/useRenderLog.js';

import ServicesSection from '../Services-screen/ServicesSection.js';

// Reused sections. Per mapping: Process = existing ProcessSection, Local help =
// existing MapSection, Guides = the Blog (rendered with your BlogCard).
import { getHomeData } from '../../Public/home-data.js';
import { getBlogsData } from '../../Public/blog-data.js';
import FeaturedGuides from '../../Utils/Cards/blog-featured-cards-component.js';
import { RevealGroup, RevealItem } from '../../Utils/Animate/reveal-component.js';
import HomeAudience from '../Home-Screen/Sections/audience-component.js';
import { getAudienceData } from '../../Public/audience-data.js';
import ProcessSection from '../../Utils/Sections/section-process-component.js';
import ScreenColorsSection from '../../Utils/Sections/section-screen-colors-component.js';
import InfoCardsSection from '../../Utils/Sections/section-info-cards-component.js';
import GallerySection from '../../Utils/Sections/section-gallery-component.js';
import AboutScreen from '../AboutUs-screren/AboutUs.js';
import QuickJoinSection from '../../Utils/Sections/section-quick-join-component.js';
import PromotionSection from '../../Utils/Sections/section-promotion-component.js';
import DonationSection from '../../Utils/Sections/section-donation-component.js';
import EventsCalendarSection from '../../Utils/Sections/section-events-calendar-component.js';
import { getEventsScreenData } from '../../Public/events-screen-data.js';
import WhoWeAreSection from '../../Utils/Sections/section-who-we-are-component.js';
import useScrollToTop from '../../Hooks/useScrollToTop';
import { scrollToHashId } from '../../Hooks/useHandleRedirect';
import ShortsList from '../../Components/shorts-list-component.js';

function Landing({ topBanner = null }) {
    useRenderLog('Landing');
    const navigate = useNavigate();
    const scrollOrNavigate = useScrollOrNavigate();
    const scrollToTop = useScrollToTop();

    const systemData = useSelector((state) => state.systemData);
    const dir = systemData.dir || 'rtl';
    const rawProducts = useSelector((s) => s.cart?.products || []);
    const LandingData = useMemo(() => getLandingData(systemData.language), [systemData.language]);
    const { heroSection, infoCardsSection, contentSection, trustSection, imageGridSection, seo, specialsBannerSection, categoriesBentoSection, processSection, reviewsSection } = LandingData;
    const eventsScreenData = useMemo(() => getEventsScreenData(systemData.language), [systemData.language]);
    const audienceData = useMemo(() => getAudienceData(systemData.language), [systemData.language]);
    const bentoCategories = useMemo(() => systemData.categories?.filter(c => c.status !== false), [systemData.categories]);

    const allProducts = useMemo(() =>
        (Array.isArray(rawProducts) ? rawProducts : []).filter(p => p.status !== 'DISABLED' && p.showInStore !== false),
        [rawProducts]);

    const categoryProductGroups = useMemo(() => {
        const cats = systemData.categories || [];
        return cats
            .filter(cat => !cat.restock)
            .map(cat => ({
                cat,
                items: allProducts.filter(p => (p.categoryIds || []).includes(cat.id) || p.category?.id === cat.id),
            }))
            .filter(({ items }) => items.length > 0);
    }, [systemData.categories, allProducts]);

    const [reviews, setReviews] = useState([]);
    const [homeData, setHomeData] = useState(null);
    const [blogs, setBlogs] = useState([]);

    useEffect(() => {
        // Deep-link with a hash (e.g. /#contact) → scroll to that section once it
        // mounts; otherwise start at the top like a normal landing.
        const hashId = window.location.hash?.replace(/^#/, '');
        if (hashId) scrollToHashId(hashId);
        else scrollToTop();
    }, []);

    // ClearViolations home sections (audience) + blog posts for the guides section.
    useEffect(() => {
        getHomeData().then(setHomeData).catch(() => { });
        getBlogsData(systemData.language).then(d => setBlogs(d || [])).catch(() => { });
        getReviewsData(systemData.language).then(d => setReviews(d || [])).catch(() => { });
    }, [systemData.language]);

    // Hero content, S3 `content.heroData` (via /home) mapped onto the component's
    // shape, per-field over the local seed so the page never blanks. Placeholder
    // strings from the HOME_LOCAL fallback are ignored (they'd overwrite real copy).
    const heroContent = useMemo(() => {
        const s3 = homeData?.hero;
        if (!s3) return heroSection;
        const filled = (v) => typeof v === 'string' && v.trim()
            && !/^(headline goes here|section title|add your content here|item (one|two|three|four|five|six))/i.test(v.trim());
        const titleLines = filled(s3.titleLine1)
            ? [{ text: s3.titleLine1, accent: false }, ...(filled(s3.titleLine2) ? [{ text: s3.titleLine2, accent: true }] : [])]
            : null;
        const chips = (Array.isArray(s3.chips) ? s3.chips.filter(filled) : []).slice(0, 4);
        return {
            ...heroSection,
            ...(filled(s3.eyebrow) ? { eyebrow: s3.eyebrow } : {}),
            ...(titleLines ? { heroTitleLines: titleLines } : {}),
            ...(filled(s3.paragraph) ? { heroSubtitle: s3.paragraph } : {}),
            ...(filled(s3.primaryCta?.label) ? { primaryCtaText: s3.primaryCta.label } : {}),
            ...(s3.primaryCta?.href ? { primaryTarget: { id: 'contact', href: s3.primaryCta.href } } : {}),
            ...(filled(s3.image) && s3.image !== '/placeholder.svg' ? { heroImage: s3.image } : {}),
            ...(chips.length ? { features: chips.map((c) => ({ title: c, icon: '' })) } : {}),
        };
    }, [homeData, heroSection]);

    // Guides = the Blog. Show real blog posts when available; until then, fall back
    // to the seeded guide cards mapped into the blog-card shape so BlogCard renders them.
    const guideBlogs = useMemo(() => {
        if (Array.isArray(blogs) && blogs.length) return blogs.slice(0, 3);
        const cards = homeData?.guides?.cards || [];
        return cards.slice(0, 3).map((g, i) => ({
            id: g.slug || i,
            slug: g.slug,
            title: g.title,
            subtitle: g.excerpt,
            category: g.category || 'GUIDE',
            image: g.image || '',
        }));
    }, [blogs, homeData]);

    return (
        <div>
            <Layout seo={{ ...seo, hasEnVersion: true }} overlayHeader>
                {topBanner}
                <LoadingAnimation fullscreen={false} />

                <div className="flex w-full h-full justify-center align-middle overflow-x-hidden transition-all delay-200">
                    <section className="bg-white dark:bg-gray-900 transition-all delay-200 w-full">


                        {/* <ShopHeroSection /> */}

                        <LandingHeroSection heroContent={heroContent} />

                        {/* Info Bar */}
                        <InfoBarSection alwaysScroll />

                        {/* Specials Banner */}
                        {/* <SpecialsBannerSection sectionData={specialsBannerSection} images={specialsBannerSection.images} mobileImages={specialsBannerSection.mobileImages} /> */}

                        {/* Categories Bento */}
                        {/* <CategoriesBentoSection sectionData={categoriesBentoSection} categories={bentoCategories} maxItems={categoriesBentoSection.maxCategories} /> */}

                        {/* <ProductFinderSection/> */}

                        {/* 3. Categories */}
                        {/* <CategoryListSection /> */}

                        {/* 6. Products, one section per category, content section injected at midpoint */}
                        {/* <div className="bg-white divide-y divide-gray-50">
                            {(() => {
                                const mid = Math.ceil(categoryProductGroups.length / 2);
                                return categoryProductGroups.map(({ cat, items }, index) => (
                                    <div key={cat.id}>
                                        <ProductListSection
                                            title={cat.name}
                                            subtitle={cat.description}
                                            items={items}
                                            limit={8}
                                            showMore={true}
                                            href={`/categories/${cat.slug}`}
                                        />
                                        {index === mid - 1 && (
                                            <div className="max-w-[1400px] mx-auto px-4 py-4">
                                                <ContentSection
                                                    label={contentSection.label}
                                                    title={contentSection.title}
                                                    subtitle={contentSection.subtitle}
                                                    backgroundImage={contentSection.backgroundImage}
                                                    buttonText={contentSection.buttonText}
                                                    onButtonClick={() => navigate('/categories')}
                                                    className="rounded-2xl overflow-hidden"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ));
                            })()}
                        </div> */}




                        {/* 4. Category banners, MaestroShield design-system split cards (carousel) */}
                        <div id="help">
                            <CategoryBanners headingLine1="Our" headingLine2="Solutions" />
                        </div>

                        {/* ── YJP-specific sections: not used for ClearViolations (kept, commented) ── */}
                        {/* <EventsCalendarSection
                            title={eventsScreenData.landing.title}
                            subtitle={eventsScreenData.landing.subtitle}
                            viewAllHref="/events"
                            showPast
                        /> */}

                        <QuickCallSection />

                        {/* Mesh color picker, interactive swatches update the preview */}
                        <LazyOnView rootMargin="-100px 0px -50px 0px" threshold={0.1} delay={0}>
                            <ScreenColorsSection />
                        </LazyOnView>

                        {/* Four simple steps → existing ProcessSection */}
                        <div id="process">
                            <LazyOnView rootMargin="-100px 0px -50px 0px" threshold={0.1} delay={0}>
                                <ProcessSection title={processSection.title} subtitle={processSection.subtitle} eyebrows={processSection.eyebrows} steps={processSection.steps} />
                            </LazyOnView>
                        </div>



                        {/* <ShortsList/> */}

                        {/* <AboutScreen /> */}

                        {/* Events calendar */}

                        {/* Membership / Promotion */}
                        {/* <PromotionSection /> */}


                        {/* Donation campaign */}
                        {/* <DonationSection /> */}

                        {/* <GallerySection /> */}

                        {/* Who We Are, About intro (self-sources its data from the About screen) */}
                        <div id="about">
                            <LazyOnView rootMargin="-100px 0px -50px 0px" threshold={0.1} delay={0}>
                                <WhoWeAreSection />
                            </LazyOnView>
                        </div>

                        {/* 11. Reviews */}
                        {reviews?.length > 0 && (
                            <section id="reviews" className="w-full bg-[#F2F3F4] py-[40px] px-4">
                                <div className="mx-auto max-w-[1400px]">
                                    <div className="max-w-2xl text-start px-2">
                                        <h2 className="font-secondary text-[32px] sm:text-[44px] font-bold leading-[1.1] tracking-[-0.025em] text-primary-500">
                                            {reviewsSection?.title}
                                        </h2>
                                        {reviewsSection?.subtitle && (
                                            <p className="mt-3 text-lg leading-relaxed text-primary-300">
                                                {reviewsSection.subtitle}
                                            </p>
                                        )}
                                    </div>
                                    <LazyOnView rootMargin="-100px 0px -50px 0px" threshold={0.1} delay={0}>
                                        <ReviewList reviews={reviews} />
                                    </LazyOnView>
                                </div>
                            </section>
                        )}

                        {/* <QuickJoinSection /> */}

                        {/* <ProcessSection /> */}


                        {/* 5. Metrics */}
                        {/* <div className="flex w-full py-20 justify-center flex-col align-middle bg-primary-700 rounded-xl lg:rounded-none">
                            <LazyOnView rootMargin="-100px 0px -50px 0px" threshold={0.1} delay={0}>
                                <MetricsSection />
                            </LazyOnView>
                        </div> */}


                        {/* 7. Image Grid */}
                        {/* <ImageGrid items={imageGridSection.items} /> */}

                        {/* 9. Map */}
                        {/* <MapSection /> */}

                        {/* 10. Content */}
                        {/* <ContentSection
                            title={<div className="whitespace-pre-line">{contentSection.title}</div>}
                            subtitle={contentSection.subtitle}
                            backgroundImage={contentSection.backgroundImage}
                            backgroundColor={'bg-white'}
                            buttonText={contentSection.buttonText}
                            onButtonClick={() => console.log('Clicked Content Section')}
                            disclaimer={contentSection.disclaimer}
                        /> */}




                        {/* 12. About */}
                        {/* <LazyOnView rootMargin="-100px 0px -50px 0px" threshold={0.1} delay={0}>
                            <About />
                        </LazyOnView> */}

                        {/* 13. Services */}
                        <div
                            id="services"
                            className="w-full relative bg-white dark:bg-black border-t border-primary-50 dark:border-neutral-800 overflow-hidden">
                            <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[radial-gradient(#003366_1px,transparent_1px)] [background-size:40px_40px]"></div>
                            <div className="relative z-10">
                                <ServicesSection
                                    isHeadline={false}
                                    limit={3}
                                    showMore={false}
                                    showMoreText={`${dir == 'rtl' ? 'עוד שירותים' : 'More Services'} →`}
                                    onShowMore={() => navigate('/services')}
                                    loading={false}
                                />
                            </div>
                        </div>

                        <QuickCallSection />


                        {/* <OurWorkSection /> */}


                        {/* Info Cards, "Why property owners call us first" (orbit) */}
                        {/* <InfoCardsSection infoCardsSection={infoCardsSection} /> */}

                        {/* Who We Help, static, code-owned content (see Public/audience-data.js) */}
                        {/* <LazyOnView rootMargin="-100px 0px -50px 0px" threshold={0.1} delay={0}>
                            <HomeAudience data={audienceData} />
                        </LazyOnView> */}

                        {/* Local help → existing MapSection */}
                        <div id="areas">
                            <LazyOnView rootMargin="-100px 0px -50px 0px" threshold={0.1} delay={0}>
                                <MapSection />
                            </LazyOnView>
                        </div>

                        {/* Guides = the Blog (your BlogCard) */}
                        {guideBlogs.length > 0 && (
                            <div id="learn" className="w-full bg-white py-[72px]">
                                <div className="mx-auto max-w-[1200px] px-6">
                                    <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
                                        <h2 className="font-secondary text-[32px] font-bold leading-[1.12] tracking-[-0.025em] text-primary-500 sm:text-[42px]">
                                            Helpful, <span className="text-secondary-500">but not overwhelming.</span>
                                        </h2>
                                        <a href="/blog" className="inline-flex items-center gap-2 rounded-full border border-primary-200 px-6 py-3 font-secondary font-semibold text-primary-500 transition hover:bg-primary-500 hover:text-white">
                                            Visit the Blog →
                                        </a>
                                    </div>
                                    <RevealGroup>
                                        <RevealItem>
                                            <FeaturedGuides blogs={guideBlogs} lang={systemData.language} />
                                        </RevealItem>
                                    </RevealGroup>
                                </div>
                            </div>
                        )}


                        {/* 14. FAQ */}
                        <div id="faq" className="w-full bg-slate-50">
                            <div className="w-full max-w-7xl mx-auto px-4">
                                <LazyOnView rootMargin="-400px 0px -50px 0px" threshold={0.1} delay={0}>
                                    <FaqSection />
                                </LazyOnView>
                            </div>
                        </div>

                        <QuickCallSection />

                        {/* 15. Contact */}
                        <div id="contact">
                            <LazyOnView rootMargin="-100px 0px -50px 0px" threshold={0.1} delay={0}>
                                <Contact />
                            </LazyOnView>
                        </div>

                    </section>
                </div>
            </Layout>
            <Footer />
        </div>
    );
}

export default Landing;
